import {Divider, Flex, Label, Text, Title} from 'exsportia-components'
import {FC, useEffect, useMemo, useState} from 'react'
import {useQuery} from 'react-query'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate, useParams} from 'react-router-dom'

import {BookingQueries} from '../../api/booking'
import {Booking} from '../../api/booking/types'
import {MemberQueries} from '../../api/member'
import {BookingCardForList} from '../../components/bookings-card-for-list/bookings-card-for-list'
import {EmptyList} from '../../components/empty-list/empty-list'
import {NavMenu} from '../../components/nav-menu/nav-menu'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {useAuthState} from '../../services/auth/hooks'
import {getWaitingListBookingsRequest} from '../../services/booking/actions'
import {WaitingListBooking} from '../../services/booking/types'
import {UserService} from '../../services/user'
import {NavigationList} from '../../ui/components/navigation-list/navigation-list'
import {TabButtonProps} from '../../ui/components/tab-button/tab-button'
import {TitledSection} from '../../ui/components/titeled-section/titled-section'
import {auth} from '../../utils/firebase'
import {useLocale, useLuxon} from '../../utils/i18n'
import {isNotEmpty, isNotNilAndNotEmpty} from '../../utils/logic'

type tabType = 'past' | 'upcoming' | 'waiting'

const Header: FC<{tabSettings: TabButtonProps[]}> = ({tabSettings}) => {
  const {l} = useLocale()
  return (
    <>
      <Flex
        display={['flex', 'none']}
        p='16px 16px 0 16px'
      >
        <Title text={l('bookings')} />
      </Flex>
      <NavMenu tabsSettings={tabSettings} />
      <Divider settings={{m: 0}} />
    </>
  )
}

const WaitingListCard: FC<{booking: WaitingListBooking}> = ({booking}) => {
  const luxon = useLuxon()
  const {l} = useLocale()
  const timestamp = Number(booking.date_timestamp)
  const dateStr = isNaN(timestamp) ? '' : luxon.fromMillis(timestamp).toUTC().toFormat('DD, HH:mm')

  return (
    <Flex
      flexDirection='column'
      p='12px 16px'
      mb='8px'
      borderRadius='12px'
      border='1px solid rgba(0, 16, 61, 0.12)'
      background='background-secondary'
      gap='4px'
    >
      <Text
        text={booking.name || l('waitingList')}
        settings={{fontWeight: 600, fontSize: '15px'}}
      />
      {dateStr ? (
        <Label>{dateStr}</Label>
      ) : null}
      <Text
        text={l('waitingList')}
        settings={{fontSize: '12px', color: 'text-secondary'}}
      />
    </Flex>
  )
}

const WaitingBookingsList: FC<{venueId?: string}> = ({venueId}) => {
  const dispatch = useDispatch()
  const {l} = useLocale()
  const userId = auth.currentUser?.uid
  const {data: user} = useQuery(UserService.queries.getUserQuery({cacheTime: 0, params: {id: userId}}))
  const {data: member, isLoading: isMemberLoading} = useQuery(
    MemberQueries.getMemberByVenueQuery({
      query: {
        venueId,
        userId,
      },
    }),
  )
  const waitingListBookings = useSelector(UserService.selectors.waitingListBookings()) as WaitingListBooking[]

  useEffect(() => {
    if (venueId && member?.uid) {
      dispatch(
        getWaitingListBookingsRequest({
          venueId,
          memberId: member.uid,
          l,
        }),
      )
    }
  }, [venueId, member?.uid, dispatch, l])

  if (isMemberLoading) {
    return (
      <NavigationList
        isLoading={true}
        Component={BookingCardForList}
        withoutChevron={true}
        elementsSettings={[]}
      />
    )
  }

  if (!Array.isArray(waitingListBookings) || waitingListBookings.length === 0) {
    return <EmptyList withoutBack={true} />
  }

  return (
    <Flex
      flexDirection='column'
      p='16px'
    >
      {waitingListBookings.map((booking, index) => (
        <WaitingListCard
          key={booking.uid || booking.id || `waiting-${index}`}
          booking={booking}
        />
      ))}
    </Flex>
  )
}

const BookingsList: FC<{activeTab: tabType}> = ({activeTab}) => {
  const {venueId} = useParams()
  const luxon = useLuxon()
  const userId = auth.currentUser?.uid
  const {data, isLoading} = useQuery(
    BookingQueries.getUserBookingsQuery({
      params: {
        userId,
      },
      query: {
        venueId,
        bookingType: activeTab,
      },
    }),
  )
  const bookingsForDisplay = useMemo(() => {
    const bookingsByDate = (data?.bookings || [])
      .filter(isNotNilAndNotEmpty)
      .reduce<{[key: string]: Booking[]}>((acc, booking) => {
      const timestamp = booking.timestamp
      const dayString = luxon.fromMillis(Number(timestamp)).toUTC().toFormat('DD')
      return {
        ...acc,
        [dayString]: [...(isNotNilAndNotEmpty(acc[dayString]) ? acc[dayString] : []), booking],
      }
    }, {})
    return Object.keys(bookingsByDate).map((dayString, sectionIndex) => (
      <TitledSection title={dayString} key={`section-${sectionIndex}`}>
          {bookingsByDate[dayString].map((booking, index) => (
          <BookingCardForList
            key={`booking-card-${index}`}
            booking={booking}
            isWaiting={activeTab === 'waiting'}
          />
        ))}
      </TitledSection>
    ))
  }, [data, activeTab])
  if (data?.bookings === null || isLoading) {
    return (
      <NavigationList
        isLoading={true}
        Component={BookingCardForList}
        withoutChevron={true}
        elementsSettings={[]}
      />
    )
  } else if (!isNotEmpty(data?.bookings)) {
    return <EmptyList withoutBack={true} />
  }
  return <>{bookingsForDisplay}</>
}

export const BookingsListPage: FC = () => {
  const {l} = useLocale()
  const {venueId} = useParams()
  const navigate = useNavigate()
  const {isLoggedIn} = useAuthState()
  const [activeTab, setActiveTab] = useState<tabType>('upcoming')
  useEffect(() => {
    if (!isLoggedIn) {
      navigate(`/${venueId}`)
    }
  }, [isLoggedIn])
  const tabSettings: TabButtonProps[] = useMemo(
    () => [
      {
        text: l('upcoming'),
        isActive: activeTab === 'upcoming',
        onClick: () => setActiveTab('upcoming'),
      },
      {
        text: l('past'),
        isActive: activeTab === 'past',
        onClick: () => setActiveTab('past'),
      },
      {
        text: l('waitingList'),
        isActive: activeTab === 'waiting',
        onClick: () => setActiveTab('waiting'),
      },
    ],
    [activeTab],
  )
  return (
    <GridLayout header={<Header tabSettings={tabSettings} />}>
      {activeTab === 'waiting' ? (
        <WaitingBookingsList venueId={venueId} />
      ) : (
        <BookingsList activeTab={activeTab} />
      )}
    </GridLayout>
  )
}
